import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { ApiError } from "@atlas/shared";
import { Download } from "lucide-react";
import { cancelChallenge, fetchChallenge, fetchChallengeShareUrl, fetchGroup, type ChallengeSummary } from "../../api/groups";
import { toShareCardImageUrl } from "../../api/wrapped";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { ShareCardButton } from "../../components/ShareCardButton";
import { Skel } from "../../components/Skeleton";
import { usePageMeta } from "../../hooks/usePageMeta";
import { GroupNotFound } from "./GroupPage";
import {
  CARD_STYLE,
  CoverHero,
  ErrorLine,
  GroupsPageFrame,
  HERO_BACK_STYLE,
  HERO_EYEBROW_STYLE,
  HERO_TEXT_SOFT,
  HERO_TITLE_STYLE,
  HeroBackLabel,
  SectionHeading,
  Standings,
  WinnerMark,
  challengeRulesLine,
  challengeUnit,
  challengeWhen,
  fmtNumber,
  fmtRange,
  groupErrorMessage,
  winnerNames,
} from "./group-ui";

// /groups/:id/challenges/:cid (spec 32 / ATL-151): standings, dates, rules,
// state and the winner's mark. A winner also sees their share card (image
// preview plus the signed link, same pattern as the Wrapped card).

const STATE_LABELS: Record<ChallengeSummary["state"], { label: string; variant: "info" | "neutral" | "success" }> = {
  upcoming: { label: "Upcoming", variant: "neutral" },
  active: { label: "On now", variant: "info" },
  ended: { label: "Ended", variant: "neutral" },
  finalised: { label: "Finished", variant: "success" },
};

function targetLine(c: ChallengeSummary): string {
  switch (c.target.type) {
    case "collection":
      return `Counts: hills, landmarks and trail sections in ${c.target.name}.`;
    case "region":
      return `Counts: hills, landmarks and trail sections inside ${c.target.name}.`;
    case "anything":
      return "Counts: hills, landmarks and trail sections anywhere.";
    default:
      return "Counts: new explored squares on the map.";
  }
}

function WinnerCard({ groupId, challengeId }: { groupId: string; challengeId: string }) {
  const shareUrlQuery = useQuery({
    queryKey: ["groups", groupId, "challenges", challengeId, "share-url"],
    queryFn: () => fetchChallengeShareUrl(groupId, challengeId),
    staleTime: Infinity,
    retry: false,
  });
  const imageUrl = shareUrlQuery.data ? toShareCardImageUrl(shareUrlQuery.data.url) : null;
  const [saving, setSaving] = React.useState<"idle" | "saving" | "error">("idle");

  // The image is on the API origin, where an <a download> is ignored, so
  // fetch it and save the blob instead.
  async function download(url: string) {
    setSaving("saving");
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error("fetch failed");
      const href = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = href;
      a.download = "atlas-challenge-win.png";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 10_000);
      setSaving("idle");
    } catch {
      setSaving("error");
      setTimeout(() => setSaving("idle"), 2000);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
      <div
        style={{
          width: "100%",
          borderRadius: "var(--radius-md)",
          overflow: "hidden",
          border: "1px solid var(--border-subtle)",
          background: "var(--surface-sunken)",
          aspectRatio: "1200 / 630",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt="Your challenge winner's card"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <span className="eyebrow" style={{ fontSize: 10 }}>
            {shareUrlQuery.isError ? "Couldn't load your card" : "Preparing your card…"}
          </span>
        )}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <ShareCardButton fetchUrl={() => fetchChallengeShareUrl(groupId, challengeId)} label="Share the win" />
        {imageUrl && (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Download size={13} />}
            disabled={saving === "saving"}
            onClick={() => void download(imageUrl)}
          >
            {saving === "error" ? "Couldn't download" : "Download image"}
          </Button>
        )}
      </div>
    </div>
  );
}

function InfoCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
      <SectionHeading title={title} />
      <div style={{ ...CARD_STYLE, padding: 16 }}>{children}</div>
    </section>
  );
}

export default function ChallengePage() {
  const { id, cid } = useParams({ strict: false }) as { id: string; cid: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const challengeQuery = useQuery({
    queryKey: ["groups", id, "challenges", cid],
    queryFn: () => fetchChallenge(id, cid),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
  });
  const groupQuery = useQuery({
    queryKey: ["groups", id],
    queryFn: () => fetchGroup(id),
    retry: false,
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelChallenge(id, cid),
    onSuccess: () => {
      // Leave first, so the cancelled challenge isn't refetched (404) here.
      void navigate({ to: "/groups/$id", params: { id } }).then(() => {
        queryClient.removeQueries({ queryKey: ["groups", id, "challenges", cid] });
        void queryClient.invalidateQueries({ queryKey: ["groups"] });
      });
    },
  });

  usePageMeta({
    title: "Challenge - Atlas",
    description: "A challenge in a private Atlas group.",
    canonicalPath: "/groups",
    noindex: true,
  });

  if (challengeQuery.isLoading) {
    return (
      <GroupsPageFrame>
        <Skel height="200px" radius="var(--radius-xl)" />
        <Skel height="300px" radius="var(--radius-lg)" />
      </GroupsPageFrame>
    );
  }
  if (challengeQuery.isError || !challengeQuery.data) return <GroupNotFound error={challengeQuery.error} />;

  const { challenge, rows, canCancel } = challengeQuery.data;
  const groupName = groupQuery.data?.group.name ?? "Group";
  const unit = challengeUnit(challenge.target.type, challenge.countRepeats);
  const finished = challenge.state === "finalised";
  const winnerIds = new Set(finished ? challenge.winners.map((w) => w.userId) : []);
  const meRow = rows.find((r) => r.isMe);
  const iWon = finished && !!meRow && winnerIds.has(meRow.userId);
  const state = STATE_LABELS[challenge.state];
  const prize =
    challenge.prize && (challenge.prizeStatus === "approved" || challenge.prizeStatus === "granted")
      ? challenge.prize.kind === "waypoints"
        ? `${fmtNumber(challenge.prize.amount ?? 0)} Waypoints`
        : (challenge.prize.itemName ?? "A store item")
      : null;

  function handleCancel() {
    if (window.confirm("Cancel this challenge? It disappears for everyone in the group.")) {
      cancelMutation.mutate();
    }
  }

  return (
    <GroupsPageFrame>
      <CoverHero
        compact
        images={challenge.target.imageUrl ? [challenge.target.imageUrl] : []}
        top={
          <Link to="/groups/$id" params={{ id }} style={HERO_BACK_STYLE}>
            <HeroBackLabel label={groupName} />
          </Link>
        }
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Badge variant={state.variant}>{state.label}</Badge>
          <span className="tnum" style={HERO_EYEBROW_STYLE}>
            {challenge.target.name} · {fmtRange(challenge.startsOn, challenge.endsOn)}
          </span>
        </div>
        <h1 style={HERO_TITLE_STYLE}>{challenge.title}</h1>
        <p className="tnum" style={{ fontSize: 14, color: HERO_TEXT_SOFT, margin: 0, textShadow: "0 1px 8px rgba(0,0,0,.35)" }}>
          {challengeWhen(challenge)}
        </p>
      </CoverHero>

      <div
        className="r-cols-1"
        style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.5fr) minmax(0, 1fr)", gap: 20, alignItems: "start" }}
      >
        <section style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
          <SectionHeading title={finished ? "Results" : "Standings"} />
          <div style={{ ...CARD_STYLE, padding: 14 }}>
            <Standings
              rows={rows}
              unit={unit}
              winnerIds={winnerIds}
              emptyHint={challenge.state === "upcoming" ? "Standings start on the first day." : "Nobody has scored yet."}
            />
          </div>
        </section>

        <div style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0 }}>
          {finished && (
            <InfoCard title="Winner">
              {challenge.winners.length === 0 ? (
                <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>Nobody scored, so nobody won.</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <WinnerMark size={22} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ font: "var(--type-h3)", fontSize: 17, color: "var(--text-primary)", overflowWrap: "anywhere" }}>
                        {winnerNames(challenge)}
                      </div>
                      <div className="tnum" style={{ fontSize: 13, color: "var(--text-muted)" }}>
                        {fmtNumber(challenge.winners[0]!.value)} {unit}
                        {challenge.winners.length > 1 ? ", a shared win" : ""}
                      </div>
                    </div>
                  </div>
                  {iWon && <WinnerCard groupId={id} challengeId={cid} />}
                </div>
              )}
            </InfoCard>
          )}

          <InfoCard title="Rules">
            <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 8, fontSize: 13.5, color: "var(--text-secondary)", lineHeight: 1.5 }}>
              <li>{targetLine(challenge)}</li>
              <li>{challengeRulesLine(challenge)}</li>
              <li className="tnum">
                {fmtRange(challenge.startsOn, challenge.endsOn)}, both days included, UK time. Members who join late
                still count from the first day.
              </li>
              <li>
                {challenge.target.type === "new_ground"
                  ? "Whoever explores the most new squares wins."
                  : challenge.countRepeats
                    ? "Whoever reaches the most wins."
                    : "Whoever collects the most wins."}{" "}
                A tie shares the win.
              </li>
              {prize && <li>Prize: {prize}, backed by Atlas and granted after the results are checked.</li>}
              {challenge.prizeStatus === "requested" && <li>A prize has been requested and is waiting on Atlas.</li>}
            </ul>
          </InfoCard>

          {canCancel && (challenge.state === "upcoming" || challenge.state === "active") && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-start" }}>
              <Button variant="ghost" size="sm" disabled={cancelMutation.isPending} onClick={handleCancel}>
                {cancelMutation.isPending ? "Cancelling…" : "Cancel challenge"}
              </Button>
              {cancelMutation.isError && (
                <ErrorLine>{groupErrorMessage(cancelMutation.error, "Couldn't cancel the challenge.")}</ErrorLine>
              )}
            </div>
          )}
        </div>
      </div>
    </GroupsPageFrame>
  );
}
